import Dialog from "../../components/General/ConfirmableDialog";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableContainer,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import dayjs from "../../utils/dayjsTimezone";
import { apiVariable } from "../../api/apiVariable";
import useAuth from "../../hooks/useAuth";
import { EmployeeDto } from "../../interfaces/HR/employee";
import { formatCurrency } from "../../utils/formatCurrency";
import { exchangeRateService } from "../../api/exchangeRateService";

interface EmployeeMonthlySalaryDto {
  id: string;
  employeeId: string;
  year: number;
  month: number;
  salaryAmount: number;
  hasSalaryIncrease: boolean;
  salaryIncreasePercentage?: number | null;
  newSalaryAmount?: number | null;
  totalLoads?: number | null;
  totalMcLeodGross?: number | null;
  dispatchRate?: number | null;
  dispatchAmount?: number | null;
  bonusAmount1?: number | null;
  bonusAmount2?: number | null;
  note?: string | null;
  exchangeRate?: number | null;
  sourceCurrency?: string | null;
  targetCurrency?: string | null;
  total: number;
  mdlTotal?: number | null;
  isDispatcher: boolean;
}

interface EmployeeFinancialFileDto {
  id: string;
  financialRecordId: string;
  financialRecordType: string;
  fileName: string;
  contentType: string;
  size: number;
  uploadedDate: string;
}

const emptySalaryForm = {
  id: "",
  year: new Date().getFullYear(),
  month: new Date().getMonth() + 1,
  salaryAmount: 0,
  hasSalaryIncrease: false,
  salaryIncreasePercentage: "",
  newSalaryAmount: "",
  totalLoads: "",
  totalMcLeodGross: "",
  dispatchRate: "",
  dispatchAmount: "",
  bonusAmount1: "",
  bonusAmount2: "",
  note: "",
  exchangeRate: "",
  sourceCurrency: "USD",
  targetCurrency: "MDL",
};

const MAX_SALARY_NOTE_LENGTH = 2000;
const ALL_DEPARTMENTS = "all";

const formatMdlCurrency = (value: number): string =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "MDL",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);

const EmployeeFinancialsPage = () => {
  const { token } = useAuth();
  const [selectedEmployeeId, setSelectedEmployeeId] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState(ALL_DEPARTMENTS);

  const [salaries, setSalaries] = useState<EmployeeMonthlySalaryDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [salaryDialogOpen, setSalaryDialogOpen] = useState(false);
  const [salaryForm, setSalaryForm] = useState(emptySalaryForm);
  const [exchangeRateLoading, setExchangeRateLoading] = useState(false);
  const skipNextRateLookup = useRef(false);
  const [salaryFilesDialogOpen, setSalaryFilesDialogOpen] = useState(false);
  const [selectedSalary, setSelectedSalary] =
    useState<EmployeeMonthlySalaryDto | null>(null);
  const [salaryFiles, setSalaryFiles] = useState<EmployeeFinancialFileDto[]>(
    [],
  );
  const [salaryFilesLoading, setSalaryFilesLoading] = useState(false);
  const [uploadingSalaryFiles, setUploadingSalaryFiles] = useState(false);
  const [downloadingFileId, setDownloadingFileId] = useState("");
  const [deletingFileId, setDeletingFileId] = useState("");

  const headers = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token],
  );

  const {
    data: employees = [],
    isLoading: employeesLoading,
    isError: isEmployeesError,
  } = useQuery({
    queryKey: ["employees", "active"],
    enabled: Boolean(token),
    queryFn: async () => {
      const { data } = await axios.get<EmployeeDto[]>(
        `${apiVariable}/getAllEmployees`,
        {
          headers,
          params: { areActive: true },
        },
      );
      return data;
    },
    staleTime: 5 * 60 * 1000,
  });

  const loadFinancialData = useCallback(
    async (employeeId: string) => {
      if (!token || !employeeId) return;

      setLoading(true);
      setError("");

      try {
        const { data } = await axios.get<EmployeeMonthlySalaryDto[]>(
          `${apiVariable}/${employeeId}/salaries`,
          { headers },
        );

        setSalaries(data);
      } catch {
        setError("Failed to load employee salaries.");
      } finally {
        setLoading(false);
      }
    },
    [headers, token],
  );

  const departments = useMemo(
    () =>
      Array.from(
        new Set(
          employees
            .map((employee) => employee.department?.trim())
            .filter((department): department is string => Boolean(department)),
        ),
      ).sort((first, second) => first.localeCompare(second)),
    [employees],
  );

  const filteredEmployees = useMemo(
    () =>
      selectedDepartment === ALL_DEPARTMENTS
        ? employees
        : employees.filter(
            (employee) => employee.department?.trim() === selectedDepartment,
          ),
    [employees, selectedDepartment],
  );

  useEffect(() => {
    if (
      selectedEmployeeId &&
      filteredEmployees.some((employee) => employee.id === selectedEmployeeId)
    ) {
      return;
    }

    setSelectedEmployeeId(filteredEmployees[0]?.id ?? "");
    if (!filteredEmployees.length) setSalaries([]);
  }, [filteredEmployees, selectedEmployeeId]);

  useEffect(() => {
    if (selectedEmployeeId) {
      loadFinancialData(selectedEmployeeId);
    }
  }, [loadFinancialData, selectedEmployeeId]);

  const resetSalaryForm = () => setSalaryForm(emptySalaryForm);

  const openAddSalary = () => {
    resetSalaryForm();
    setSalaryDialogOpen(true);
  };

  const openEditSalary = (salary: EmployeeMonthlySalaryDto) => {
    // Preserve a historical/manual value when opening an existing record. A lookup
    // only runs after the user changes one of the lookup fields.
    skipNextRateLookup.current = true;
    setSalaryForm({
      id: salary.id,
      year: salary.year,
      month: salary.month,
      salaryAmount: salary.salaryAmount,
      hasSalaryIncrease: salary.hasSalaryIncrease,
      salaryIncreasePercentage:
        salary.salaryIncreasePercentage?.toString() ?? "",
      newSalaryAmount: salary.newSalaryAmount?.toString() ?? "",
      totalLoads: salary.totalLoads?.toString() ?? "",
      totalMcLeodGross: salary.totalMcLeodGross?.toString() ?? "",
      dispatchRate: salary.dispatchRate?.toString() ?? "",
      dispatchAmount: salary.dispatchAmount?.toString() ?? "",
      bonusAmount1: salary.bonusAmount1?.toString() ?? "",
      bonusAmount2: salary.bonusAmount2?.toString() ?? "",
      note: salary.note ?? "",
      exchangeRate: salary.exchangeRate?.toString() ?? "",
      sourceCurrency: salary.sourceCurrency ?? "",
      targetCurrency: salary.targetCurrency ?? "",
    });
    setSalaryDialogOpen(true);
  };

  useEffect(() => {
    if (!salaryDialogOpen) return;
    if (skipNextRateLookup.current) {
      skipNextRateLookup.current = false;
      return;
    }

    const sourceCurrency = salaryForm.sourceCurrency.trim();
    const targetCurrency = salaryForm.targetCurrency.trim();
    if (
      !sourceCurrency ||
      !targetCurrency ||
      !salaryForm.year ||
      !salaryForm.month
    ) {
      setSalaryForm((previous) => ({ ...previous, exchangeRate: "" }));
      return;
    }

    const controller = new AbortController();
    setExchangeRateLoading(true);
    exchangeRateService
      .lookup(
        Number(salaryForm.year),
        Number(salaryForm.month),
        sourceCurrency,
        targetCurrency,
        headers,
        controller.signal,
      )
      .then(({ data }) =>
        setSalaryForm((previous) => ({
          ...previous,
          exchangeRate: data?.rate?.toString() ?? "",
        })),
      )
      .catch((requestError) => {
        if (!axios.isCancel(requestError))
          setSalaryForm((previous) => ({ ...previous, exchangeRate: "" }));
      })
      .finally(() => {
        if (!controller.signal.aborted) setExchangeRateLoading(false);
      });

    return () => controller.abort();
  }, [
    headers,
    salaryDialogOpen,
    salaryForm.month,
    salaryForm.sourceCurrency,
    salaryForm.targetCurrency,
    salaryForm.year,
  ]);

  const saveSalary = async () => {
    if (!selectedEmployeeId || !token) return;

    const commonPayload = {
      id: salaryForm.id || undefined,
      employeeId: selectedEmployeeId,
      year: Number(salaryForm.year),
      month: Number(salaryForm.month),
      isDispatcher,
      note: salaryForm.note.trim() || null,
      exchangeRate:
        salaryForm.exchangeRate === "" ? null : Number(salaryForm.exchangeRate),
      sourceCurrency: salaryForm.sourceCurrency.trim() || null,
      targetCurrency: salaryForm.targetCurrency.trim() || null,
    };

    if (salaryForm.note.length > MAX_SALARY_NOTE_LENGTH) {
      setError(
        `Salary note cannot exceed ${MAX_SALARY_NOTE_LENGTH} characters.`,
      );
      return;
    }

    if (
      salaryForm.exchangeRate !== "" &&
      Number(salaryForm.exchangeRate) <= 0
    ) {
      setError("Exchange rate must be greater than 0.");
      return;
    }
    const payload = isDispatcher
      ? {
          ...commonPayload,
          totalLoads: Number(salaryForm.totalLoads),
          totalMcLeodGross: Number(salaryForm.totalMcLeodGross),
          dispatchRate: Number(salaryForm.dispatchRate),
          dispatchAmount: Number(salaryForm.dispatchAmount),
          bonusAmount1:
            salaryForm.bonusAmount1 === ""
              ? null
              : Number(salaryForm.bonusAmount1),
          bonusAmount2:
            salaryForm.bonusAmount2 === ""
              ? null
              : Number(salaryForm.bonusAmount2),
        }
      : {
          ...commonPayload,
          salaryAmount: Number(salaryForm.salaryAmount),
          hasSalaryIncrease: salaryForm.hasSalaryIncrease,
          salaryIncreasePercentage:
            salaryForm.salaryIncreasePercentage === ""
              ? null
              : Number(salaryForm.salaryIncreasePercentage),
          newSalaryAmount:
            salaryForm.newSalaryAmount === ""
              ? null
              : Number(salaryForm.newSalaryAmount),
        };

    try {
      if (salaryForm.id) {
        await axios.put(`${apiVariable}/salaries`, payload, { headers });
      } else {
        await axios.post(`${apiVariable}/salaries`, payload, { headers });
      }

      setSalaryDialogOpen(false);
      await loadFinancialData(selectedEmployeeId);
    } catch {
      setError("Failed to save salary.");
    }
  };

  const deleteSalary = async (salaryId: string) => {
    if (!selectedEmployeeId || !token) return;

    try {
      await axios.delete(`${apiVariable}/salaries/${salaryId}`, { headers });
      await loadFinancialData(selectedEmployeeId);
    } catch {
      setError("Failed to delete salary.");
    }
  };

  const loadSalaryFiles = useCallback(
    async (salaryId: string) => {
      setSalaryFilesLoading(true);
      try {
        const response = await axios.get<EmployeeFinancialFileDto[]>(
          `${apiVariable}/employeeFinancials/salary/${salaryId}/files`,
          { headers },
        );
        setSalaryFiles(response.data);
      } catch {
        setError("Failed to load salary files.");
      } finally {
        setSalaryFilesLoading(false);
      }
    },
    [headers],
  );

  const openSalaryFiles = async (salary: EmployeeMonthlySalaryDto) => {
    setSelectedSalary(salary);
    setSalaryFiles([]);
    setSalaryFilesDialogOpen(true);
    await loadSalaryFiles(salary.id);
  };

  const uploadSalaryFiles = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    event.target.value = "";
    if (!selectedSalary || files.length === 0) return;

    const formData = new FormData();
    files.forEach((file) => formData.append("files", file));
    setUploadingSalaryFiles(true);
    setError("");
    try {
      await axios.post(
        `${apiVariable}/employeeFinancials/salary/${selectedSalary.id}/upload`,
        formData,
        { headers },
      );
      await loadSalaryFiles(selectedSalary.id);
    } catch {
      setError(
        "Failed to upload salary files. Only PDF, JPG, JPEG, PNG, XLS, and XLSX files are allowed.",
      );
    } finally {
      setUploadingSalaryFiles(false);
    }
  };

  const downloadSalaryFile = async (file: EmployeeFinancialFileDto) => {
    setDownloadingFileId(file.id);
    setError("");
    try {
      const response = await axios.get(
        `${apiVariable}/employeeFinancials/files/${file.id}/download`,
        { headers, responseType: "blob" },
      );
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = file.fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch {
      setError(`Failed to download '${file.fileName}'.`);
    } finally {
      setDownloadingFileId("");
    }
  };

  const deleteSalaryFile = async (file: EmployeeFinancialFileDto) => {
    if (!selectedSalary || !window.confirm(`Delete '${file.fileName}'?`))
      return;

    setDeletingFileId(file.id);
    setError("");
    try {
      await axios.delete(`${apiVariable}/employeeFinancials/files/${file.id}`, {
        headers,
      });
      await loadSalaryFiles(selectedSalary.id);
    } catch {
      setError(`Failed to delete '${file.fileName}'.`);
    } finally {
      setDeletingFileId("");
    }
  };

  const formatFileSize = (size: number) => {
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  };

  const selectedEmployee = employees.find(
    (employee) => employee.id === selectedEmployeeId,
  );

  const isDispatcher = Boolean(
    selectedEmployee?.position?.toLowerCase().includes("dispatcher") ||
    selectedEmployee?.position?.toLowerCase().includes("dispatch"),
  );

  const yearToDateTotals = useMemo(() => {
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth() + 1;

    return salaries
      .filter(
        (salary) => salary.year === currentYear && salary.month <= currentMonth,
      )
      .reduce(
        (totals, salary) => {
          const dispatchAmount = salary.dispatchAmount ?? 0;
          const bonusAmount1 = salary.bonusAmount1 ?? 0;
          const bonusAmount2 = salary.bonusAmount2 ?? 0;

          totals.totalLoads += salary.totalLoads ?? 0;
          totals.totalMcLeodGross += salary.totalMcLeodGross ?? 0;
          totals.dispatchAmount += dispatchAmount;
          totals.bonusAmount1 += bonusAmount1;
          totals.bonusAmount2 += bonusAmount2;
          totals.total += dispatchAmount + bonusAmount1 + bonusAmount2;
          return totals;
        },
        {
          totalLoads: 0,
          totalMcLeodGross: 0,
          dispatchAmount: 0,
          bonusAmount1: 0,
          bonusAmount2: 0,
          total: 0,
        },
      );
  }, [salaries]);

  const salaryFormTotal = isDispatcher
    ? Number(salaryForm.dispatchAmount || 0) +
      Number(salaryForm.bonusAmount1 || 0) +
      Number(salaryForm.bonusAmount2 || 0)
    : Number(salaryForm.salaryAmount || 0);
  const salaryFormMdlTotal =
    salaryForm.exchangeRate === ""
      ? null
      : Math.round(
          (salaryFormTotal * Number(salaryForm.exchangeRate) + Number.EPSILON) *
            100,
        ) / 100;

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Employee Financials
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel id="employee-financials-department-label">
            Department
          </InputLabel>
          <Select
            labelId="employee-financials-department-label"
            value={selectedDepartment}
            label="Department"
            onChange={(event) => setSelectedDepartment(event.target.value)}
          >
            <MenuItem value={ALL_DEPARTMENTS}>All Departments</MenuItem>
            {departments.map((department) => (
              <MenuItem key={department} value={department}>
                {department}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Autocomplete
          options={filteredEmployees}
          loading={employeesLoading}
          loadingText="Loading employees..."
          value={selectedEmployee ?? null}
          onChange={(_, employee) => setSelectedEmployeeId(employee?.id ?? "")}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          getOptionLabel={(employee) =>
            `${employee.firstName} ${employee.lastName}`.trim()
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Employee"
              placeholder="Search employee"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {employeesLoading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
              fullWidth
            />
          )}
        />

        {!employeesLoading && !filteredEmployees.length && (
          <Typography sx={{ mt: 1 }} color="text.secondary">
            No employees found in this department.
          </Typography>
        )}

        {selectedEmployee && (
          <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>
            Position: {selectedEmployee.position} • Department:{" "}
            {selectedEmployee.department}
          </Typography>
        )}
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {isEmployeesError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load employees.
        </Alert>
      )}

      {loading ? (
        <Box
          sx={{
            flexWrap: { xs: "wrap", lg: "nowrap" },
            display: "flex",
            justifyContent: "center",
            py: 4,
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={2}>
          {isDispatcher && (
            <Grid item xs={12}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Year-to-Date Summary
                </Typography>
                <Grid container spacing={2}>
                  {[
                    [
                      "Total Loads",
                      yearToDateTotals.totalLoads.toLocaleString(),
                    ],
                    [
                      "McLeod Gross",
                      formatCurrency(yearToDateTotals.totalMcLeodGross),
                    ],
                    [
                      "Dispatch Amount",
                      formatCurrency(yearToDateTotals.dispatchAmount),
                    ],
                    [
                      "Bonus Amount 1",
                      formatCurrency(yearToDateTotals.bonusAmount1),
                    ],
                    [
                      "Bonus Amount 2",
                      formatCurrency(yearToDateTotals.bonusAmount2),
                    ],
                    ["Total", formatCurrency(yearToDateTotals.total)],
                  ].map(([label, value]) => (
                    <Grid item xs={12} sm={6} md={4} key={label}>
                      <Typography variant="body2" color="text.secondary">
                        {label}
                      </Typography>
                      <Typography variant="h6">{value}</Typography>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            </Grid>
          )}
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Stack
                flexWrap={{ xs: "wrap", lg: "nowrap" }}
                useFlexGap
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 2 }}
              >
                <Typography variant="h6">Monthly Salaries</Typography>
                <Button
                  variant="contained"
                  onClick={openAddSalary}
                  disabled={!selectedEmployeeId}
                >
                  Add Salary
                </Button>
              </Stack>

              <TableContainer
                sx={{
                  maxWidth: "100%",
                  overflowX: "auto",
                  overscrollBehaviorX: "contain",
                }}
              >
                <Table size="small" sx={{ minWidth: 900 }}>
                  <TableHead>
                    {isDispatcher ? (
                      <TableRow>
                        <TableCell>Year</TableCell>
                        <TableCell>Month</TableCell>
                        <TableCell>Total Loads</TableCell>
                        <TableCell>Total McLeod Gross</TableCell>
                        <TableCell>Dispatch Rate</TableCell>
                        <TableCell>Dispatch Amount</TableCell>
                        <TableCell>Bonus Amount 1</TableCell>
                        <TableCell>Bonus Amount 2</TableCell>
                        <TableCell>Total</TableCell>
                        <TableCell>Exchange Rate</TableCell>
                        <TableCell>MDL Total</TableCell>
                        <TableCell>Note</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    ) : (
                      <TableRow>
                        <TableCell>Year</TableCell>
                        <TableCell>Month</TableCell>
                        <TableCell>Salary</TableCell>
                        <TableCell>Increase</TableCell>
                        <TableCell>New Salary</TableCell>
                        <TableCell>Exchange Rate</TableCell>
                        <TableCell>MDL Total</TableCell>
                        <TableCell>Note</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    )}
                  </TableHead>
                  <TableBody>
                    {salaries.map((salary) => (
                      <TableRow key={salary.id}>
                        <TableCell>{salary.year}</TableCell>
                        <TableCell>{salary.month}</TableCell>
                        {isDispatcher ? (
                          <>
                            <TableCell>{salary.totalLoads ?? 0}</TableCell>
                            <TableCell>
                              {formatCurrency(salary.totalMcLeodGross ?? 0)}
                            </TableCell>
                            <TableCell>{salary.dispatchRate ?? 0}%</TableCell>
                            <TableCell>
                              {formatCurrency(salary.dispatchAmount ?? 0)}
                            </TableCell>
                            <TableCell>
                              {formatCurrency(salary.bonusAmount1 ?? 0)}
                            </TableCell>
                            <TableCell>
                              {formatCurrency(salary.bonusAmount2 ?? 0)}
                            </TableCell>
                            <TableCell>
                              {formatCurrency(
                                (salary.dispatchAmount ?? 0) +
                                  (salary.bonusAmount1 ?? 0) +
                                  (salary.bonusAmount2 ?? 0),
                              )}
                            </TableCell>
                          </>
                        ) : (
                          <>
                            <TableCell>
                              {formatCurrency(salary.salaryAmount)}
                            </TableCell>
                            <TableCell>
                              {salary.hasSalaryIncrease
                                ? salary.salaryIncreasePercentage != null
                                  ? `${salary.salaryIncreasePercentage}%`
                                  : "Yes"
                                : "No"}
                            </TableCell>
                            <TableCell>
                              {salary.newSalaryAmount != null
                                ? formatCurrency(salary.newSalaryAmount)
                                : "-"}
                            </TableCell>
                          </>
                        )}
                        <TableCell>
                          {salary.exchangeRate != null
                            ? salary.exchangeRate.toLocaleString("en-US", {
                                maximumFractionDigits: 8,
                              })
                            : "—"}
                        </TableCell>
                        <TableCell>
                          {salary.exchangeRate != null
                            ? formatMdlCurrency(
                                Math.round(
                                  ((isDispatcher
                                    ? (salary.dispatchAmount ?? 0) +
                                      (salary.bonusAmount1 ?? 0) +
                                      (salary.bonusAmount2 ?? 0)
                                    : salary.salaryAmount) *
                                    salary.exchangeRate +
                                    Number.EPSILON) *
                                    100,
                                ) / 100,
                              )
                            : "—"}
                        </TableCell>
                        <TableCell
                          sx={{
                            minWidth: 180,
                            maxWidth: 280,
                            whiteSpace: "pre-wrap",
                            overflowWrap: "anywhere",
                          }}
                        >
                          {salary.note || "—"}
                        </TableCell>
                        <TableCell align="right">
                          <Stack
                            flexWrap={{ xs: "wrap", lg: "nowrap" }}
                            useFlexGap
                            direction="row"
                            justifyContent="flex-end"
                            spacing={1}
                          >
                            <Button
                              size="small"
                              onClick={() => openEditSalary(salary)}
                            >
                              Edit
                            </Button>
                            <Button
                              size="small"
                              onClick={() => openSalaryFiles(salary)}
                            >
                              Files
                            </Button>
                            <Button
                              size="small"
                              color="error"
                              onClick={() => deleteSalary(salary.id)}
                            >
                              Delete
                            </Button>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                    {!salaries.length && (
                      <TableRow>
                        <TableCell
                          colSpan={isDispatcher ? 13 : 9}
                          align="center"
                        >
                          No salary records.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>
      )}

      <Dialog
        open={salaryFilesDialogOpen}
        onClose={() => setSalaryFilesDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Salary Files
          {selectedSalary
            ? ` — ${selectedSalary.month}/${selectedSalary.year}`
            : ""}
        </DialogTitle>
        <DialogContent>
          <Stack
            flexWrap={{ xs: "wrap", lg: "nowrap" }}
            useFlexGap
            direction="row"
            justifyContent="flex-end"
            sx={{ mb: 2 }}
          >
            <Button
              component="label"
              variant="contained"
              disabled={uploadingSalaryFiles}
            >
              {uploadingSalaryFiles ? "Uploading..." : "Upload Files"}
              <input
                hidden
                multiple
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.xls,.xlsx,application/pdf,image/jpeg,image/png,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={uploadSalaryFiles}
              />
            </Button>
          </Stack>
          {salaryFilesLoading ? (
            <Box
              sx={{
                flexWrap: { xs: "wrap", lg: "nowrap" },
                display: "flex",
                justifyContent: "center",
                py: 4,
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer
              sx={{
                maxWidth: "100%",
                overflowX: "auto",
                overscrollBehaviorX: "contain",
              }}
            >
              <Table size="small" sx={{ minWidth: 900 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>File</TableCell>
                    <TableCell>Size</TableCell>
                    <TableCell>Uploaded</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {salaryFiles.map((file) => (
                    <TableRow key={file.id}>
                      <TableCell>{file.fileName}</TableCell>
                      <TableCell>{formatFileSize(file.size)}</TableCell>
                      <TableCell>
                        {dayjs(file.uploadedDate).format("MM/DD/YYYY h:mm A")}
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          disabled={downloadingFileId === file.id}
                          onClick={() => downloadSalaryFile(file)}
                        >
                          {downloadingFileId === file.id
                            ? "Downloading..."
                            : "Download"}
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          disabled={deletingFileId === file.id}
                          onClick={() => deleteSalaryFile(file)}
                        >
                          {deletingFileId === file.id
                            ? "Deleting..."
                            : "Delete"}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!salaryFiles.length && (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        No files uploaded for this salary record.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSalaryFilesDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={salaryDialogOpen}
        onClose={() => setSalaryDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {salaryForm.id ? "Edit Salary" : "Add Salary"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="number"
                label="Year"
                value={salaryForm.year}
                onChange={(event) =>
                  setSalaryForm((prev) => ({
                    ...prev,
                    year: Number(event.target.value),
                  }))
                }
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="number"
                label="Month"
                value={salaryForm.month}
                onChange={(event) =>
                  setSalaryForm((prev) => ({
                    ...prev,
                    month: Number(event.target.value),
                  }))
                }
              />
            </Grid>
            {isDispatcher ? (
              <>
                {(
                  [
                    ["totalLoads", "Total Loads"],
                    ["totalMcLeodGross", "Total McLeod Gross"],
                    ["dispatchRate", "Dispatch Rate"],
                    ["dispatchAmount", "Dispatch Amount"],
                    ["bonusAmount1", "Bonus Amount 1"],
                    ["bonusAmount2", "Bonus Amount 2"],
                  ] as const
                ).map(([field, label]) => (
                  <Grid item xs={12} sm={6} key={field}>
                    <TextField
                      fullWidth
                      type="number"
                      label={label}
                      value={salaryForm[field]}
                      inputProps={{
                        min: 0,
                        step: field === "totalLoads" ? 1 : 0.01,
                      }}
                      onChange={(event) =>
                        setSalaryForm((prev) => ({
                          ...prev,
                          [field]: event.target.value,
                        }))
                      }
                    />
                  </Grid>
                ))}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Total"
                    value={formatCurrency(salaryFormTotal)}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
              </>
            ) : (
              <>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Salary Amount"
                    value={salaryForm.salaryAmount}
                    onChange={(event) =>
                      setSalaryForm((prev) => ({
                        ...prev,
                        salaryAmount: Number(event.target.value),
                      }))
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel id="has-increase-label">
                      Has Salary Increase
                    </InputLabel>
                    <Select
                      labelId="has-increase-label"
                      value={salaryForm.hasSalaryIncrease ? "yes" : "no"}
                      label="Has Salary Increase"
                      onChange={(event) =>
                        setSalaryForm((prev) => ({
                          ...prev,
                          hasSalaryIncrease: event.target.value === "yes",
                        }))
                      }
                    >
                      <MenuItem value="no">No</MenuItem>
                      <MenuItem value="yes">Yes</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Increase %"
                    value={salaryForm.salaryIncreasePercentage}
                    onChange={(event) =>
                      setSalaryForm((prev) => ({
                        ...prev,
                        salaryIncreasePercentage: event.target.value,
                      }))
                    }
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="New Salary Amount"
                    value={salaryForm.newSalaryAmount}
                    onChange={(event) =>
                      setSalaryForm((prev) => ({
                        ...prev,
                        newSalaryAmount: event.target.value,
                      }))
                    }
                  />
                </Grid>
              </>
            )}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Source Currency"
                value={salaryForm.sourceCurrency}
                inputProps={{ maxLength: 3 }}
                onChange={(event) =>
                  setSalaryForm((prev) => ({
                    ...prev,
                    sourceCurrency: event.target.value.toUpperCase(),
                  }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Target Currency"
                value={salaryForm.targetCurrency}
                inputProps={{ maxLength: 3 }}
                onChange={(event) =>
                  setSalaryForm((prev) => ({
                    ...prev,
                    targetCurrency: event.target.value.toUpperCase(),
                  }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Exchange Rate"
                value={salaryForm.exchangeRate}
                error={
                  salaryForm.exchangeRate !== "" &&
                  Number(salaryForm.exchangeRate) <= 0
                }
                helperText={
                  exchangeRateLoading
                    ? "Looking up saved monthly rate..."
                    : "Optional; enter a rate manually when no saved rate exists"
                }
                inputProps={{ min: 0.00000001, step: 0.00000001 }}
                onChange={(event) =>
                  setSalaryForm((prev) => ({
                    ...prev,
                    exchangeRate: event.target.value,
                  }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="MDL Total"
                value={
                  salaryFormMdlTotal == null
                    ? "—"
                    : formatMdlCurrency(salaryFormMdlTotal)
                }
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Note"
                value={salaryForm.note}
                error={salaryForm.note.length > MAX_SALARY_NOTE_LENGTH}
                helperText={`${salaryForm.note.length}/${MAX_SALARY_NOTE_LENGTH}`}
                inputProps={{ maxLength: MAX_SALARY_NOTE_LENGTH }}
                onChange={(event) =>
                  setSalaryForm((prev) => ({
                    ...prev,
                    note: event.target.value,
                  }))
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSalaryDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={saveSalary}
            disabled={
              salaryForm.note.length > MAX_SALARY_NOTE_LENGTH ||
              (salaryForm.exchangeRate !== "" &&
                Number(salaryForm.exchangeRate) <= 0)
            }
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmployeeFinancialsPage;
